import { useIdle } from '@uidotdev/usehooks';
import { useMemo } from 'react';

import { MATCHA_POLLING_IDLE_TIMEOUT_MS } from '@/constants/polling';

import type { Price, Quote } from '@/utils/0x.types';

import { useIntentsPrice } from './useIntentsPrice';
import { useIntentsQuote } from './useIntentsQuote';
import { useMatchaWallets } from './useMatchaWallets';
import { UseSwapParams } from './useSwap';
import { useTokenBalance } from './useTokenBalance';

export const useIntents = ({ query, options, isGasless, telemetry }: UseSwapParams) => {
  const { ethereumWallet } = useMatchaWallets();
  const { sellTokenChainId, buyTokenChainId } = query ?? {};
  const leafParams = useMemo(
    () =>
      query
        ? {
            ...query,
            isGasless,
            telemetry: {
              buyAmountDecimal: telemetry.buyAmountDecimal,
              buyAmountUsd: telemetry.buyAmountUsd,
            },
          }
        : null,
    [query, isGasless, telemetry.buyAmountDecimal, telemetry.buyAmountUsd],
  );
  const { data: balance } = useTokenBalance({
    address: ethereumWallet?.address,
    tokenAddress: query?.sellTokenAddress,
    chainId: query?.sellTokenChainId,
  });
  const hasInsufficientBalance = !balance || balance.value < BigInt(query?.sellAmount ?? '0');
  const isIdle = useIdle(MATCHA_POLLING_IDLE_TIMEOUT_MS);
  const shouldFetch = !isIdle;

  const canSwap = !!(query && parseInt(query.sellAmount, 10) > 0);
  const canSwapSingleChain = canSwap && sellTokenChainId === buyTokenChainId;
  const canFetchQuote =
    shouldFetch && canSwapSingleChain && !!ethereumWallet?.address && !hasInsufficientBalance;
  const canFetchPrice = shouldFetch && canSwapSingleChain && !canFetchQuote;

  const {
    data: intentsPriceData,
    error: intentsPriceError,
    isLoading: isLoadingIntentsPrice,
    isFetching: isFetchingIntentsPrice,
  } = useIntentsPrice(leafParams, { ...options, enabled: canFetchPrice });

  const {
    data: intentsQuoteData,
    error: intentsQuoteError,
    isLoading: isLoadingIntentsQuote,
    isFetching: isFetchingIntentsQuote,
  } = useIntentsQuote(leafParams, { ...options, enabled: canFetchQuote });

  const data: Price | Quote | undefined = intentsQuoteData ?? intentsPriceData ?? undefined;

  return {
    data,
    error: intentsPriceError || intentsQuoteError,
    isLoading: isLoadingIntentsPrice || isLoadingIntentsQuote,
    isFetching: isFetchingIntentsPrice || isFetchingIntentsQuote,
  };
};
